<script module lang="ts">
	export type FormState = null | 'processing' | 'success' | 'error';
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import type { ChewieUMCouponIdentifier } from './api/chewieUM.types';
	import Button from './buttons/v3/button.svelte';
	import { event } from './utils/tracking';
	import type { ActionResult } from '@sveltejs/kit';
	import { identify } from '$lib/segment/client';
	import { enhance } from '$app/forms';
	import { page } from '$app/state';
	import { slide } from 'svelte/transition';
	import { emailCapture } from './state/emailCapture.svelte';
	import { checkoutSession } from './state/checkoutSession.svelte';
	import { getCouponValue } from './utils/couponCode';
	import { hashEmail } from './utils/email';
	import { pushToDataLayer } from './utils/gtm';

	interface Props {
		buttonLabel?: string;
		couponIdentifier?: ChewieUMCouponIdentifier;
		couponText?: string;
		state?: FormState;
		placeholderText?: string;
		autofocus?: boolean;
	}

	let {
		buttonLabel = 'Submit',
		couponIdentifier = 'NONE',
		couponText = '',
		state: formState = $bindable(null),
		placeholderText = 'Enter your email here',
		autofocus = false
	}: Props = $props();

	let error = $state<string | null>(null);

	const handleEnhance = async () => {
		formState = 'processing';

		if (couponIdentifier === 'free-shipping') {
			await checkoutSession.configure({
				shippingId: undefined
			});
		}

		return async (response: {
			result: ActionResult<{
				email: string;
				couponCode?: string;
				couponValue?: number;
				emailVerificationResult?: string;
			}>;
		}) => {
			const { result } = response;

			if (result.type === 'failure') {
				error = result.data?.message || null;
				formState = 'error';
				return;
			}

			if (result.type !== 'success') {
				formState = 'error';
				return;
			}

			formState = 'success';
			sessionStorage.setItem(
				'mill-modal-dismissed',
				JSON.stringify({ name: 'email-capture', reason: 'success' })
			);
			emailCapture.enter();

			if (!result.data) return;

			if (result.data.couponCode) {
				await checkoutSession.applyCoupon(result.data.couponCode);
			}

			const hashedEmail = await hashEmail(result.data.email);

			pushToDataLayer({
				order: {
					email: result.data.email,
					hashed_email: hashedEmail,
					couponCode: result.data.couponCode ?? ''
				}
			});

			await identify({ email: result.data.email });
			await event('Email Captured', {
				email: result.data.email,
				hashed_email: hashedEmail,
				emailSource: 'modal',
				emailVerificationResult: result.data.emailVerificationResult,
				couponIdentifier,
				offer: couponText,
				referringPage: window.location.pathname,
				discountCode: result.data.couponCode ?? '',
				discountType: 'amount',
				discountValue:
					result.data.couponValue ??
					(checkoutSession.data.product?.promotionCode?.coupon
						? getCouponValue(checkoutSession.data.product.promotionCode.coupon)
						: 0)
			});
		};
	};

	let inputEl = $state<HTMLInputElement>();

	onMount(() => {
		if (autofocus) inputEl?.focus();
	});
</script>

<form
	class="flex items-center rounded-full border border-brown-5 bg-brown-6 p-1 focus-within:border-brown-4/30"
	method="POST"
	action="/?/emailCapture"
	use:enhance={handleEnhance}
>
	<input type="hidden" name="couponIdentifier" value={couponIdentifier} />
	{#if couponText.length > 0}
		<input type="hidden" name="couponInfoText" value={couponText} />
	{/if}
	<input type="hidden" name="pathname" value={page.url.pathname} />
	<input
		bind:this={inputEl}
		class="ml-6 mr-3 w-full bg-transparent text-[16px] tracking-[-.16px] text-brown-1 outline-none placeholder:text-brown-1/50"
		required
		type="email"
		name="email"
		inputmode="email"
		autocomplete="email"
		placeholder={placeholderText}
	/>
	<Button type="submit" disabled={formState === 'processing'}
		>{formState === 'processing' ? 'Sending...' : buttonLabel}</Button
	>
</form>
{#if formState === 'error' && error}
	<p class="mt-2 pl-6 text-[12px]/[100%] text-red-500" transition:slide>{error}</p>
{/if}

<style lang="scss">
	form:has(input:autofill) {
		background-color: theme('colors.stone.5');
	}

	input:autofill,
	input:autofill:hover,
	input:autofill:focus,
	input:autofill:active {
		box-shadow: 0 0 0 30px theme('colors.stone.5') inset !important;
	}
</style>
